import isEqual from 'lodash.isequal';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import { isMobile } from 'react-device-detect';
import { useForm, useWatch } from 'react-hook-form';
import styled from 'styled-components';

import { FIELD_KEYS } from '_constants/forms';
import { theme } from '_constants/theme';
import { getFieldProps } from '_helpers/forms/fields';
import { usePrevious } from '_hooks';

import Button from 'components/shared/Button';
import Spinner from 'components/shared/Spinner';
import BaseField from 'components/shared/fields/BaseField';
import Text from 'components/shared/layouts/Text';

const StyledMessageContainer = styled.div`
  display: inline-flex;
  justify-content: flex-start;
  align-items: center;
  gap: ${theme.spaces.regular}px;
`;

const ChangeUsernameForm = ({ loading, error, submitDisabled, defaultValues, onSubmit }) => {
  const {
    control,
    formState: { isSubmitted, isSubmitSuccessful, submitCount },
    reset,
    handleSubmit,
  } = useForm({
    defaultValues,
    reValidateMode: 'onChange',
  });

  const prevDefaultValues = usePrevious(defaultValues);
  const values = useWatch({ control });

  useEffect(() => {
    if (isSubmitted && !isSubmitSuccessful && submitCount) {
      onSubmit();
    }
  }, [isSubmitted, isSubmitSuccessful, submitCount, onSubmit]);

  useEffect(() => {
    if (!isEqual(prevDefaultValues, defaultValues)) {
      reset(defaultValues);
    }
  }, [prevDefaultValues, defaultValues, reset]);

  return (
    <Form noValidate onSubmit={handleSubmit(onSubmit)}>
      <Row>
        <Col xs={12}>
          <BaseField
            {...getFieldProps(FIELD_KEYS.username)}
            label="Username"
            placeholder="Your Username"
            control={control}
            bottomSpace={theme.spaces.regular}
          />
        </Col>
        <Col xs={12} md={4}>
          <Button
            type="submit"
            disabled={submitDisabled || isEqual(defaultValues, values)}
            color={theme.colors.cyanBlue}
          >
            Save
          </Button>
        </Col>
        <Col xs={12} md={8}>
          {loading && (
            <Text
              size={theme.fontSizes.small}
              color={theme.colors.gray.secondary}
              topSpace={isMobile ? theme.spaces.regular : undefined}
            >
              <StyledMessageContainer>
                <Spinner />
                <div>Updating...</div>
              </StyledMessageContainer>
            </Text>
          )}
        </Col>
      </Row>

      {error && (
        <Text size={theme.fontSizes.small} color={theme.colors.red.primary} topSpace={theme.spaces.regular}>
          {error?.message}
        </Text>
      )}
    </Form>
  );
};

ChangeUsernameForm.propTypes = {
  loading: PropTypes.bool,
  error: PropTypes.object,
  submitDisabled: PropTypes.bool,
  defaultValues: PropTypes.object,
  onSubmit: PropTypes.func,
};

export default ChangeUsernameForm;
